import { useEffect, useMemo, useState } from 'react';

import ar from './locales/ar.json';
import en from './locales/en.json';

export type SupportedLocale = 'en' | 'ar';
export type Direction = 'ltr' | 'rtl';

type Dictionary = Record<string, string>;

const dictionaries: Record<SupportedLocale, Dictionary> = {
  en,
  ar,
};

const DEFAULT_LOCALE: SupportedLocale = 'en';
let currentLocale: SupportedLocale = DEFAULT_LOCALE;
const listeners = new Set<(locale: SupportedLocale) => void>();

function canUseDom() {
  return typeof document !== 'undefined';
}

function normalizeLocale(input?: string | null): SupportedLocale {
  if (!input) {
    return DEFAULT_LOCALE;
  }

  const normalized = String(input).toLowerCase();
  if (normalized.startsWith('ar')) {
    return 'ar';
  }
  return 'en';
}

function applyDocumentDirection(locale: SupportedLocale) {
  if (!canUseDom()) {
    return;
  }

  const direction: Direction = isRtlLocale(locale) ? 'rtl' : 'ltr';
  document.documentElement.lang = locale;
  document.documentElement.dir = direction;
}

function notifyLocaleSubscribers(locale: SupportedLocale) {
  listeners.forEach((listener) => listener(locale));
}

export function detectLocale(preferredLocale?: string | null): SupportedLocale {
  if (preferredLocale) {
    return normalizeLocale(preferredLocale);
  }

  if (typeof navigator !== 'undefined') {
    if (navigator.language) {
      return normalizeLocale(navigator.language);
    }
    if (Array.isArray(navigator.languages) && navigator.languages.length > 0) {
      return normalizeLocale(navigator.languages[0]);
    }
  }

  return DEFAULT_LOCALE;
}

export function initializeI18n(preferredLocale?: string | null): SupportedLocale {
  currentLocale = detectLocale(preferredLocale);
  applyDocumentDirection(currentLocale);
  return currentLocale;
}

export function getCurrentLocale(): SupportedLocale {
  return currentLocale;
}

export function isRtlLocale(locale: SupportedLocale = currentLocale): boolean {
  return locale === 'ar';
}

export function getDirection(locale: SupportedLocale = currentLocale): Direction {
  return isRtlLocale(locale) ? 'rtl' : 'ltr';
}

export function setLocale(locale: SupportedLocale): SupportedLocale {
  currentLocale = locale;
  applyDocumentDirection(locale);
  notifyLocaleSubscribers(locale);
  return currentLocale;
}

export function toggleLocale(): SupportedLocale {
  return setLocale(currentLocale === 'en' ? 'ar' : 'en');
}

export function subscribeToLocaleChange(listener: (locale: SupportedLocale) => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function t(
  key: string,
  locale: SupportedLocale = currentLocale,
  fallback?: string
): string {
  const localeDictionary = dictionaries[locale] || dictionaries.en;
  return localeDictionary[key] || dictionaries.en[key] || fallback || key;
}

export function formatHijriDate(
  value: Date | string | number,
  locale: SupportedLocale = currentLocale
): string {
  const date = value instanceof Date ? value : new Date(value);
  const localeTag = locale === 'ar' ? 'ar-SA-u-ca-islamic' : 'en-SA-u-ca-islamic';
  return new Intl.DateTimeFormat(localeTag, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

export function useI18n() {
  const [locale, setLocaleState] = useState<SupportedLocale>(getCurrentLocale());

  useEffect(() => subscribeToLocaleChange(setLocaleState), []);

  const translate = useMemo(
    () => (key: string, fallback?: string) => t(key, locale, fallback),
    [locale]
  );

  return {
    locale,
    direction: getDirection(locale),
    isRtl: isRtlLocale(locale),
    setLocale,
    toggleLocale,
    t: translate,
  };
}
